'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS, Role, RoleWithTasks, User } from '@repo/shared/domain';
import { EventEmitter, getVerifyPermission, guardedClient as request } from '@repo/shared/helpers';
import RolesWithTasksTab from 'src/parts/tabs/RolesWithTasksTab';
import { getUserRolesWithTasks, getUser } from 'src/api/admin/users';
import { getRolesForAutocomplete } from 'src/api/admin/roles';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

export const Route = createFileRoute('/_admin/users/$userId/roles')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			status: (search.status as string) || undefined,
		};
	},
	loaderDeps: ({ search: { status } }) => ({ status }),
	loader: async ({ params, deps }) => {
		const { userId } = params;
		const query = deps.status ? `status=${deps.status}` : undefined;
		const [user, roles, rolesWithTasks] = await Promise.all([
			getUser(userId),
			getRolesForAutocomplete(),
			getUserRolesWithTasks(userId, query),
		]);
		return { user, roles, rolesWithTasks };
	},
	component: RolesTab,
});

function RolesTab() {
	const { userId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user: initialUser, roles: initialRoles, rolesWithTasks: initialRolesWithTasks } = Route.useLoaderData();
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { status } = Route.useSearch();

	const query = status ? `status=${status}` : undefined;

	const { data: user } = useQuery({
		queryKey: ['user', userId],
		queryFn: () => getUser(userId),
		initialData: initialUser,
	});

	const { data: roles } = useQuery({
		queryKey: ['roles-autocomplete'],
		queryFn: () => getRolesForAutocomplete(),
		initialData: initialRoles,
	});

	const { data: rolesWithTasks, refetch: refetchRolesWithTasks } = useQuery({
		queryKey: ['user-roles-with-tasks', userId, query],
		queryFn: () => getUserRolesWithTasks(userId, query),
		initialData: initialRolesWithTasks,
	});

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasFullAccess = verifyPermission([PERMISSIONS.adminUsersEdit]);

	const handleReload = () => {
		refetchRolesWithTasks();
	};

	const handleRoleRevoke = async (roleIds: string[]) => {
		try {
			await request.post(`/api/roles/users/${userId}/revoke-batch`, {
				roleIds,
			});
			EventEmitter.emit('toast', {
				type: 'success',
				message: roleIds.length > 1 ? 'Roles removed' : 'Role removed',
			});
			queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
			queryClient.invalidateQueries({ queryKey: ['user-tasks', userId] });
			queryClient.invalidateQueries({ queryKey: ['user-roles-with-tasks', userId] });
		} catch (_e) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: roleIds.length > 1 ? 'Error deleting roles' : 'Error deleting role',
			});
		}
	};

	const handleRoleRestore = async (roleId: string) => {
		try {
			await request.put(`/api/roles/${roleId}/restore/users/${userId}`, {});
			queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
			queryClient.invalidateQueries({ queryKey: ['user-tasks', userId] });
			queryClient.invalidateQueries({ queryKey: ['user-roles-with-tasks', userId] });
		} catch (_err) {
			console.error(_err);
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to restore role.',
			});
		}
	};

	const handleRolesAdd = async (roleIds: string[]) => {
		try {
			await request.post(`/api/users/${userId}/roles`, { roleIds });
			queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
			queryClient.invalidateQueries({ queryKey: ['user-tasks', userId] });
			queryClient.invalidateQueries({ queryKey: ['user-roles-with-tasks', userId] });
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to add roles.',
			});
		}
	};

	const handleTaskSelected = (taskId: string) => {
		navigate({ to: `/users/$userId/tasks/$taskId`, params: { userId, taskId }, search: { userId } });
	};

	if (!user || !roles) {
		return null;
	}

	return (
		<RolesWithTasksTab
			authenticatedUser={authenticatedUser}
			user={user}
			roles={roles}
			readOnly={!hasFullAccess}
			rolesWithTasks={rolesWithTasks?.data || []}
			onTaskSelected={handleTaskSelected}
			onRolesAdd={handleRolesAdd}
			onRoleRevoke={handleRoleRevoke}
			onRoleRestore={handleRoleRestore}
			onReload={handleReload}
		/>
	);
}
